/**
 * The identity row of Figma `Player Card v7` (node 80:15690) — the one piece
 * every variant of that card shares.
 *
 * The v7 component has two variants in the design and both open with exactly
 * this row: a 24px initials disc, an identity column (name over a position chip
 * and a meta line), and a GPR pill pinned right. The SUGGESTION variant then
 * adds reason chips and an action row; the WATCHLIST variant adds nothing at
 * all and is this row inside a card. Keeping the row here is what stops the two
 * from drifting — a name that truncates on one surface and wraps on the other
 * is the failure this prevents.
 *
 * The row is pure presentation and takes pre-composed strings. It does not know
 * which pool surfaced the player, and it must not: the two pools are fed by
 * different generated types, and the formatters in `utils/addPlayersRowMeta`
 * are the single place that difference is reconciled.
 * @remarks View component — no business logic, hooks, or state.
 * @module features/player-kanban/components/molecules/AddPlayersRow
 */

import React, { memo } from "react";

import { Avatar, AvatarFallbackText } from "@/components/atoms/Avatar";
import { TonePill } from "@/components/atoms/Badge";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** Props for {@link PlayerCardIdentityView}. */
export interface PlayerCardIdentityViewProps {
  /** Display name. */
  readonly name: string;
  /** Pre-composed secondary line (club · age, plus contract on watchlist rows). */
  readonly meta: string;
  /**
   * Position abbreviation for the chip left of the meta line. Empty when the
   * player has no position — the chip is omitted, never blank.
   */
  readonly position?: string;
  /**
   * GPR score for the tone pill on the right edge. `null` when the player is
   * unscored — the pill is omitted rather than showing a zero.
   */
  readonly gpr?: string | null;
}

/**
 * Render the shared Player Card v7 identity row.
 *
 * ## Avatar is initials-only BY DESIGN
 *
 * The design node carries no image fill, no image paint and no photo slot — the
 * avatar is a 24px near-black disc holding white initials, which is also
 * Raniel's standing decision for player imagery. So this is not a stopgap
 * awaiting an image pipeline; adding one would diverge from the design.
 *
 * ## GPR is roster metadata, not a reason
 *
 * The pill shows the same number every player row in the app shows. On the
 * suggestion variant it sits beside the reason chips but is not one of them —
 * reasons come only from `SuggestionReasonCategory`, and presenting GPR as a
 * recommendation rationale is what SE-5523 forbids.
 * @param root0 - Component props.
 * @param root0.name - Display name.
 * @param root0.meta - Secondary line.
 * @param root0.position - Position abbreviation for the chip.
 * @param root0.gpr - GPR score for the tone pill.
 */
const PlayerCardIdentityView = ({
  name,
  meta,
  position = "",
  gpr = null,
}: PlayerCardIdentityViewProps) => (
  <Stack direction="horizontal" className="items-center gap-2">
    {/* `xs` is exactly the design's 24px disc. The near-black fill and white
        initials are the design's own values, expressed as the inverse
        surface/content pair. */}
    <Avatar size="xs" className="shrink-0 bg-surface-inverse">
      <AvatarFallbackText className="text-content-inverse">
        {name}
      </AvatarFallbackText>
    </Avatar>

    {/* The identity column's 1px name→meta step is the artboard's own value
        (`gap-px` is on the closed scale); it reads as "one block", not two
        stacked lines. */}
    <Stack className="min-w-0 flex-1 gap-px">
      {/* `body-strong` is 14px + `font-medium`, which maps to the SatoshiMedium
          (500) face the artboard asks for — medium, NOT bold. */}
      <Text variant="body-strong" className="text-content-primary" isTruncated>
        {name}
      </Text>

      <Stack direction="horizontal" className="items-center gap-1">
        {position === "" ? null : (
          <Box className="shrink-0 rounded-sm bg-surface-raised px-2 py-1">
            <Text variant="caption" className="text-content-primary">
              {position}
            </Text>
          </Box>
        )}
        {/* 12px (`caption`) for both the chip and this line. They rendered at
            10px (`micro`) until the artboard pinned them at 12 — `micro` is the
            10px rung, which is a size below what the design ever asks for on
            this card. */}
        <Text variant="caption" className="text-content-muted" isTruncated>
          {meta}
        </Text>
      </Stack>
    </Stack>

    {gpr === null ? null : (
      <TonePill
        tone="success"
        label={gpr}
        textVariant="body"
        className="h-8 w-10 shrink-0"
      />
    )}
  </Stack>
);

PlayerCardIdentityView.displayName = "PlayerCardIdentityView";

export default memo(PlayerCardIdentityView);
